import { useForm } from "react-hook-form";
import { Alert, Button, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import { useBookEvent } from "../../api/mutations/useBookEvent";
import { useSendEventBookingConfirmation } from "../../api/mutations/useSendEventBookingConfirmation";
import { useQueryClient } from "@tanstack/react-query";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetBookedEvents } from "../../api/queries/useGetBookedEvents";
import { BookedEvents } from "../../types/Types";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useTranslation } from "react-i18next";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import {
  canUserViewEvent,
  isMembershipAccessCheckPending,
} from "../../utils/eventMembershipVisibility";
import {
  eventEligibleForMemberTierDiscount,
  isPaidExecutiveMember,
} from "../../utils/eventMemberDiscount";
import { ensureExplorerProfileSyncedForEventBooking } from "../../explorer/explorerProfileSync";
import {
  EVENT_OBJECTIVES_OPTIONS,
  EVENT_OTHER_SECTOR_VALUE,
  EVENT_SECTOR_OPTIONS,
  EVENT_STAGE_OPTIONS,
} from "../../i18n/eventRegistrationI18n";

const EventSuccessModal = lazy(() => import("./EventSuccessModal"));

const EVENT_DATA_KEY = "event-data";
const CONFIRM_BOOKING_PATH = "/confirm-booking";
const EVENT_BOOKING_PATH = "/event-booking";

type FormValues = {
  stageOfBusiness?: string;
  objectives: string;
  sectorsOfInterest?: string[];
  consentToShare: boolean;
};

interface ExecutiveTicketToastProps {
  onClose: () => void;
}

const ExecutiveTicketToast = memo(function ExecutiveTicketToast({
  onClose,
}: ExecutiveTicketToastProps) {
  const { t } = useTranslation();
  return (
    <Alert
      variant="success"
      className="mb-4 rounded-4 shadow-lg d-flex flex-column flex-md-row justify-content-between align-items-center gap-2"
    >
      <div className="text-center text-md-left">
        {t("events.executiveToastMessage")}
      </div>
      <button
        type="button"
        className="btn-close"
        aria-label={t("common.close")}
        onClick={onClose}
      />
    </Alert>
  );
});
ExecutiveTicketToast.displayName = "ExecutiveTicketToast";

function EventQuestions() {
  const { t, i18n } = useTranslation();

  const sortedStageOptions = useMemo(
    () =>
      [...EVENT_STAGE_OPTIONS].sort((a, b) =>
        t(a.labelKey).localeCompare(t(b.labelKey), i18n.language),
      ),
    [t, i18n.language],
  );

  const sortedObjectivesOptions = useMemo(
    () =>
      [...EVENT_OBJECTIVES_OPTIONS].sort((a, b) =>
        t(a.labelKey).localeCompare(t(b.labelKey), i18n.language),
      ),
    [t, i18n.language],
  );

  const sortedSectorOptions = useMemo(() => {
    const withoutOther = EVENT_SECTOR_OPTIONS.filter(
      (o) => o.value !== EVENT_OTHER_SECTOR_VALUE,
    );
    const sorted = [...withoutOther].sort((a, b) =>
      t(a.labelKey).localeCompare(t(b.labelKey), i18n.language),
    );
    const other = EVENT_SECTOR_OPTIONS.find(
      (o) => o.value === EVENT_OTHER_SECTOR_VALUE,
    );
    return other ? [...sorted, other] : sorted;
  }, [t, i18n.language]);
  const { objectId, user } = useAppContext();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showTicketToast, setShowTicketToast] = useState(true);
  const eventBookingSubmittedRef = useRef(false);
  const { data: bookedEvents } = useGetBookedEvents({ id: objectId || "" });
  const { data, isError: isEventLoadError } = useGetEvent({
    eventId: eventId || "",
  });
  const { data: memberships } = useGetMemberships();

  const membershipAccessPending = useMemo(
    () =>
      isMembershipAccessCheckPending(
        data?.allowedMembershipIds,
        user?.user?.membershipName,
        memberships,
      ),
    [data?.allowedMembershipIds, user?.user?.membershipName, memberships],
  );

  const canAccessEvent = useMemo(() => {
    if (!data || membershipAccessPending) return true;
    return canUserViewEvent(
      data.allowedMembershipIds,
      user?.user?.membershipId,
      {
        membershipName: user?.user?.membershipName,
        memberships,
      },
    );
  }, [
    data,
    membershipAccessPending,
    user?.user?.membershipId,
    user?.user?.membershipName,
    memberships,
  ]);

  useEffect(() => {
    if (isEventLoadError) {
      navigate(EVENT_BOOKING_PATH, { replace: true });
    }
  }, [isEventLoadError, navigate]);

  useEffect(() => {
    eventBookingSubmittedRef.current = false;
  }, [eventId]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const { mutate: sendBookingConfirmationMail } =
    useSendEventBookingConfirmation();

  const onBookSuccess = useCallback(
    (bookResult: unknown) => {
      queryClient.invalidateQueries({
        queryKey: ["booked-events", bookResult],
      });
      localStorage.removeItem(EVENT_DATA_KEY);

      const toEmail = user?.user?.email ?? "";
      if (toEmail.trim()) {
        const fullName =
          `${user?.user?.firstName ?? ""} ${user?.user?.lastName ?? ""}`.trim() ||
          "Member";
        const eventName = data?.name || "SMBF Event";
        const eventDateText = data?.eventDate
          ? new Date(data.eventDate).toLocaleString()
          : "TBC";
        const parsedId = eventId ? parseInt(eventId, 10) : NaN;
        sendBookingConfirmationMail({
          ...(Number.isFinite(parsedId) && parsedId > 0
            ? { eventId: parsedId }
            : {}),
          email: toEmail,
          name: fullName,
          firstName: user?.user?.firstName ?? undefined,
          eventName,
          eventDateText,
        });
      }

      setShowSuccessModal(true);
    },
    [
      queryClient,
      user?.user?.email,
      user?.user?.firstName,
      user?.user?.lastName,
      data?.name,
      data?.eventDate,
      eventId,
      sendBookingConfirmationMail,
    ],
  );
  const { mutate: bookEvent } = useBookEvent({ onSuccess: onBookSuccess });

  const filteredEvents = useMemo(() => {
    const raw = bookedEvents as unknown;
    const list: BookedEvents[] = Array.isArray(raw)
      ? raw
      : raw
        ? [raw as BookedEvents]
        : [];

    return list.filter((event) => {
      if (!event?.eventDate || !data?.eventDate) return false;

      const eventDate = new Date(event.eventDate);
      const targetDate = new Date(data.eventDate);

      const eventMonth = eventDate.getMonth();
      const eventYear = eventDate.getFullYear();
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();

      return eventMonth === targetMonth && eventYear === targetYear;
    });
  }, [bookedEvents, data?.eventDate]);

  const isExecutiveMember = useMemo(
    () => isPaidExecutiveMember(user?.user),
    [user?.user],
  );

  const memberDiscountEligible = eventEligibleForMemberTierDiscount(
    data?.memberEventDiscountEligible,
  );

  const shouldShowToast = useMemo(
    () =>
      showTicketToast &&
      filteredEvents.length < 1 &&
      isExecutiveMember &&
      memberDiscountEligible,
    [
      showTicketToast,
      filteredEvents.length,
      isExecutiveMember,
      memberDiscountEligible,
    ],
  );

  const onSubmit = useCallback(
    async (formData: FormValues) => {
      localStorage.setItem(EVENT_DATA_KEY, JSON.stringify(formData));
      if (
        filteredEvents.length > 0 ||
        !isExecutiveMember ||
        !memberDiscountEligible
      ) {
        navigate(`${CONFIRM_BOOKING_PATH}/${eventId}`);
      } else {
        if (eventBookingSubmittedRef.current) return;
        eventBookingSubmittedRef.current = true;
        try {
          await ensureExplorerProfileSyncedForEventBooking();
        } catch {
          /* best-effort before AddEventAttendee merge */
        }
        bookEvent({
          stageOfBusiness: formData.stageOfBusiness,
          eventObjectives: formData.objectives,
          sectorsOfInterest: (formData.sectorsOfInterest ?? []).join(", "),
          consentToShareProfile: formData.consentToShare ? 1 : 0,
          eventId: eventId ? parseInt(eventId, 10) : 0,
          userId: objectId || "",
        });
      }
    },
    [
      filteredEvents.length,
      isExecutiveMember,
      memberDiscountEligible,
      navigate,
      eventId,
      bookEvent,
      objectId,
    ],
  );

  const handleCloseToast = useCallback(() => {
    setShowTicketToast(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  if ((!canAccessEvent && !membershipAccessPending) || isEventLoadError) {
    return (
      <main className="container p-4" aria-label="Event registration questions">
        <Alert variant="warning">{t("events.membershipNotAvailable")}</Alert>
      </main>
    );
  }

  return (
    <main
      className="container p-4"
      aria-label={t("events.registrationQuestionsAria")}
    >
      {shouldShowToast && <ExecutiveTicketToast onClose={handleCloseToast} />}

      <div className="card shadow-lg rounded-4 border-0">
        <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
          <Form.Group controlId="stageOfBusiness" className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.stageOfBusiness")}
            </Form.Label>
            <Form.Select {...register("stageOfBusiness")}>
              <option value="">{t("explorer.segmentation.select")}</option>
              {sortedStageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="objectives" className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.objectivesQuestion")} <span>*</span>
            </Form.Label>
            <Form.Select
              {...register("objectives", {
                required: t("events.thisFieldRequired"),
              })}
            >
              <option value="">{t("explorer.segmentation.select")}</option>
              {sortedObjectivesOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </Form.Select>
            {errors.objectives && (
              <Form.Text className="text-danger">
                {errors.objectives.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.sectorsOfInterest")}
            </Form.Label>
            <div className="form-checkbox-list">
              {sortedSectorOptions.map((sector, index) => {
                const fieldId = `sector-${index}`;
                return (
                  <Form.Check
                    key={sector.value}
                    id={fieldId}
                    className="form-checkbox-list__item"
                    type="checkbox"
                    value={sector.value}
                    label={t(sector.labelKey)}
                    {...register("sectorsOfInterest", {
                      validate: (value) =>
                        (Array.isArray(value) && value.length > 0) ||
                        t("events.atLeastOneSector"),
                    })}
                  />
                );
              })}
            </div>
            {errors.sectorsOfInterest && (
              <Form.Text className="text-danger">
                {errors.sectorsOfInterest.message}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              {t("events.consentQuestion")} <span>*</span>
            </Form.Label>
            <Form.Check
              id="consentToShare"
              type="checkbox"
              label={t("events.yesConsent")}
              {...register("consentToShare", {
                required: t("events.consentRequired"),
              })}
            />
            {errors.consentToShare && (
              <Form.Text className="text-danger">
                {errors.consentToShare.message}
              </Form.Text>
            )}
          </Form.Group>

          <div className="d-flex justify-content-end mt-5">
            <Button className="px-4" type="submit">
              {t("common.post")}
            </Button>
          </div>
        </Form>
      </div>
      {showSuccessModal && (
        <Suspense fallback={null}>
          <EventSuccessModal
            show={showSuccessModal}
            onHide={handleCloseModal}
            text={t("events.bookingConfirmed")}
          />
        </Suspense>
      )}
    </main>
  );
}

EventQuestions.displayName = "EventQuestions";

export default memo(EventQuestions);
